import React, { useState, useEffect, useCallback } from 'react'
import { getLocalApiUrl, authFetch } from '../config/api'
import { BarChart2, Table2, Plus, Banknote } from 'lucide-react'

const P = { pri: '#0a5169', priM: '#e8f2f6' }
const fmt$ = (n) => '$' + (Number(n) ?? 0).toLocaleString()

/**
 * Browse metering and gaming data by venue for pattern analysis.
 * Separate from linked transactions - can add selected rows to evidence.
 * defaultVenue: pre-select when case has known venue (from case.venue_name, evidence, or alert venue_code).
 */
export default function CaseBrowseData({ caseId, onAdded, defaultVenue }) {
  const [venues, setVenues] = useState([])
  const [venue, setVenue] = useState('')
  const [from, setFrom] = useState('')
  const [to, setTo] = useState('')
  const [amountLt, setAmountLt] = useState('')
  const [amountGt, setAmountGt] = useState('')
  const [subTab, setSubTab] = useState('sessions') // 'sessions' | 'meter' | 'payouts'
  const [sessions, setSessions] = useState([])
  const [meterReadings, setMeterReadings] = useState([])
  const [payouts, setPayouts] = useState([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [selectedSessions, setSelectedSessions] = useState(new Set())
  const [selectedMeter, setSelectedMeter] = useState(new Set())
  const [selectedPayouts, setSelectedPayouts] = useState(new Set())
  const [adding, setAdding] = useState(false)

  useEffect(() => {
    let cancelled = false
    ;(async () => {
      try {
        const r = await authFetch(getLocalApiUrl('/api/venues'))
        const j = await r.json()
        if (j.success && Array.isArray(j.data) && !cancelled) setVenues(j.data)
      } catch {}
    })()
    return () => { cancelled = true }
  }, [])

  // Reset venue when case changes
  useEffect(() => { setVenue('') }, [caseId])

  // Auto-select venue: from defaultVenue prop, or when only one venue exists
  useEffect(() => {
    if (venue) return // user already selected
    const resolved = defaultVenue || (venues.length === 1 ? (venues[0]?.name || venues[0]?.venue_name) : null)
    if (resolved) setVenue(resolved)
  }, [caseId, defaultVenue, venues, venue])

  const venueOptions = venues.map((v) => ({ id: v.id, name: v.name || v.venue_name || String(v.id) }))
  const venueFilter = venue

  const loadSessions = useCallback(async () => {
    if (!venueFilter) return
    setLoading(true)
    setError(null)
    try {
      const qs = new URLSearchParams()
      if (venueFilter) qs.set('venue', venueFilter)
      if (from) qs.set('from', from)
      if (to) qs.set('to', to)
      qs.set('limit', '100')
      const r = await authFetch(getLocalApiUrl(`/api/gaming/sessions?${qs}`))
      const j = await r.json()
      if (!j.success) throw new Error(j.error || 'Failed to load sessions')
      setSessions(j.data || [])
      setSelectedSessions(new Set())
    } catch (e) {
      setError(e.message)
      setSessions([])
    } finally {
      setLoading(false)
    }
  }, [venueFilter, from, to])

  const loadMeterReadings = useCallback(async () => {
    if (!venueFilter) return
    setLoading(true)
    setError(null)
    try {
      const qs = new URLSearchParams()
      if (venueFilter) qs.set('venue', venueFilter)
      if (from) qs.set('from', from)
      if (to) qs.set('to', to)
      qs.set('limit', '100')
      const r = await authFetch(getLocalApiUrl(`/api/gaming/egms/readings?${qs}`))
      const j = await r.json()
      if (!j.success) throw new Error(j.error || 'Failed to load meter readings')
      setMeterReadings(j.data || [])
      setSelectedMeter(new Set())
    } catch (e) {
      setError(e.message)
      setMeterReadings([])
    } finally {
      setLoading(false)
    }
  }, [venueFilter, from, to])

  const loadPayouts = useCallback(async () => {
    if (!venueFilter) return
    setLoading(true)
    setError(null)
    try {
      const qs = new URLSearchParams()
      if (venueFilter) qs.set('venue', venueFilter)
      if (from) qs.set('from', from)
      if (to) qs.set('to', to)
      if (amountLt) qs.set('amount_lt', amountLt)
      if (amountGt) qs.set('amount_gt', amountGt)
      qs.set('limit', '100')
      const r = await authFetch(getLocalApiUrl(`/api/gaming/payouts?${qs}`))
      const j = await r.json()
      if (!j.success) throw new Error(j.error || 'Failed to load payouts')
      setPayouts(j.data || [])
      setSelectedPayouts(new Set())
    } catch (e) {
      setError(e.message)
      setPayouts([])
    } finally {
      setLoading(false)
    }
  }, [venueFilter, from, to, amountLt, amountGt])

  const load = useCallback(() => {
    if (subTab === 'sessions') loadSessions()
    else if (subTab === 'meter') loadMeterReadings()
    else if (subTab === 'payouts') loadPayouts()
  }, [subTab, loadSessions, loadMeterReadings, loadPayouts])

  useEffect(() => {
    if (venueFilter) load()
  }, [venueFilter, load])

  const toggleSession = (pk) => {
    setSelectedSessions((prev) => {
      const next = new Set(prev)
      if (next.has(pk)) next.delete(pk)
      else next.add(pk)
      return next
    })
  }

  const toggleMeter = (key) => {
    setSelectedMeter((prev) => {
      const next = new Set(prev)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })
  }

  const togglePayout = (pk) => {
    setSelectedPayouts((prev) => {
      const next = new Set(prev)
      if (next.has(pk)) next.delete(pk)
      else next.add(pk)
      return next
    })
  }

  const handleAddToEvidence = useCallback(async () => {
    if (!caseId) return
    const items = []
    if (subTab === 'sessions') {
      for (const pk of selectedSessions) {
        const s = sessions.find((x) => String(x.SessionPK) === String(pk))
        if (s)
          items.push({
            type: 'session',
            payload: s,
            source_table: 'gaming_sessions',
            SessionPK: s.SessionPK,
            StartTime: s.StartTime,
          })
      }
    } else if (subTab === 'payouts') {
      for (const pk of selectedPayouts) {
        const p = payouts.find((x) => String(x.TransactionPK) === String(pk))
        if (p)
          items.push({
            type: 'transaction',
            payload: p,
            source_table: 'gaming_transactions',
            TransactionPK: p.TransactionPK,
            SnapShotTime: p.SnapShotTime,
          })
      }
    } else {
      for (const key of selectedMeter) {
        const m = meterReadings.find((x) => `${x.EgmPK}-${x.SnapshotTime}` === key)
        if (m)
          items.push({
            type: 'meter_reading',
            payload: m,
            source_table: 'egm_meter_readings',
            SnapshotTime: m.SnapshotTime,
          })
      }
    }
    if (items.length === 0) return
    setAdding(true)
    try {
      const r = await authFetch(getLocalApiUrl(`/api/cases/${caseId}/evidence/add`), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items }),
      })
      const j = await r.json()
      if (!j.success) throw new Error(j.error || 'Failed to add evidence')
      setSelectedSessions(new Set())
      setSelectedMeter(new Set())
      setSelectedPayouts(new Set())
      onAdded?.()
    } catch (e) {
      setError(e.message)
    } finally {
      setAdding(false)
    }
  }, [caseId, subTab, selectedSessions, selectedMeter, selectedPayouts, sessions, meterReadings, payouts, onAdded])

  const selectedCount = subTab === 'sessions' ? selectedSessions.size : subTab === 'payouts' ? selectedPayouts.size : selectedMeter.size
  const hasSelection = selectedCount > 0

  return (
    <div className="space-y-4">
      <div className="text-xs text-slate-500">
        Browse metering and gaming data by venue for pattern analysis. Add selected rows to evidence (separate from linked transactions).
      </div>

      <div className="card p-4">
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 items-end">
          <div>
            <label className="block text-[11px] font-bold text-slate-500 uppercase tracking-wider mb-1">Venue</label>
            <input
              list="browse-venue-list"
              className="input-field w-full text-sm"
              placeholder="Type or select venue"
              value={venue}
              onChange={(e) => setVenue(e.target.value)}
            />
            <datalist id="browse-venue-list">
              {venueOptions.map((v) => (
                <option key={v.id} value={v.name} />
              ))}
            </datalist>
          </div>
          <div>
            <label className="block text-[11px] font-bold text-slate-500 uppercase tracking-wider mb-1">From</label>
            <input
              type="datetime-local"
              className="input-field w-full text-sm"
              value={from}
              onChange={(e) => setFrom(e.target.value)}
            />
          </div>
          <div>
            <label className="block text-[11px] font-bold text-slate-500 uppercase tracking-wider mb-1">To</label>
            <input
              type="datetime-local"
              className="input-field w-full text-sm"
              value={to}
              onChange={(e) => setTo(e.target.value)}
            />
          </div>
          {subTab === 'payouts' && (
            <>
              <div>
                <label className="block text-[11px] font-bold text-slate-500 uppercase tracking-wider mb-1">Payout &lt; $</label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  placeholder="e.g. 500"
                  className="input-field w-24 text-sm"
                  value={amountLt}
                  onChange={(e) => setAmountLt(e.target.value)}
                />
              </div>
              <div>
                <label className="block text-[11px] font-bold text-slate-500 uppercase tracking-wider mb-1">Payout &gt; $</label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  placeholder="e.g. 100"
                  className="input-field w-24 text-sm"
                  value={amountGt}
                  onChange={(e) => setAmountGt(e.target.value)}
                />
              </div>
            </>
          )}
          <div className="flex gap-2">
            <button
              type="button"
              onClick={load}
              className="btn-primary text-sm py-2 px-4"
            >
              Apply
            </button>
            {hasSelection && (
              <button
                type="button"
                onClick={handleAddToEvidence}
                disabled={adding}
                className="text-sm py-2 px-4 rounded-md border font-semibold flex items-center gap-1"
                style={{ borderColor: P.pri, background: P.priM, color: P.pri }}
              >
                {adding ? 'Adding…' : <><Plus size={14} /> Add {selectedCount} to evidence</>}
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="flex gap-2 border-b border-slate-200 mb-2">
        <button
          type="button"
          onClick={() => { setSubTab('sessions'); setError(null); if (venueFilter) loadSessions(); }}
          className={`flex items-center gap-1.5 py-2 px-4 text-xs font-bold rounded-t-lg ${subTab === 'sessions' ? 'bg-gray-100' : ''}`}
          style={{ color: subTab === 'sessions' ? P.pri : '#94a3b8' }}
        >
          <Table2 size={14} /> Gaming Sessions
        </button>
        <button
          type="button"
          onClick={() => { setSubTab('meter'); setError(null); if (venueFilter) loadMeterReadings(); }}
          className={`flex items-center gap-1.5 py-2 px-4 text-xs font-bold rounded-t-lg ${subTab === 'meter' ? 'bg-gray-100' : ''}`}
          style={{ color: subTab === 'meter' ? P.pri : '#94a3b8' }}
        >
          <BarChart2 size={14} /> Meter Readings
        </button>
        <button
          type="button"
          onClick={() => { setSubTab('payouts'); setError(null); if (venueFilter) loadPayouts(); }}
          className={`flex items-center gap-1.5 py-2 px-4 text-xs font-bold rounded-t-lg ${subTab === 'payouts' ? 'bg-gray-100' : ''}`}
          style={{ color: subTab === 'payouts' ? P.pri : '#94a3b8' }}
        >
          <Banknote size={14} /> Payouts
        </button>
      </div>

      {error && <div className="text-sm text-red-600">{error}</div>}

      <div className="card overflow-hidden">
        {subTab === 'sessions' && (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-3 py-2 w-10" />
                  <th className="px-3 py-2 text-left">SessionPK</th>
                  <th className="px-3 py-2 text-left">MemberID</th>
                  <th className="px-3 py-2 text-left">EgmName</th>
                  <th className="px-3 py-2 text-left">Venue</th>
                  <th className="px-3 py-2 text-left">StartTime</th>
                  <th className="px-3 py-2 text-left">Turnover</th>
                  <th className="px-3 py-2 text-left">CreditWin</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 bg-white">
                {loading && (
                  <tr><td colSpan={8} className="px-3 py-6 text-center text-slate-500">Loading…</td></tr>
                )}
                {!loading && sessions.map((r) => (
                  <tr key={r.SessionPK} className="hover:bg-slate-50">
                    <td className="px-3 py-2">
                      <input
                        type="checkbox"
                        checked={selectedSessions.has(String(r.SessionPK))}
                        onChange={() => toggleSession(String(r.SessionPK))}
                      />
                    </td>
                    <td className="px-3 py-2 font-mono">{r.SessionPK}</td>
                    <td className="px-3 py-2">{r.MemberID || '—'}</td>
                    <td className="px-3 py-2">{r.EgmName || '—'}</td>
                    <td className="px-3 py-2">{r.Venue || '—'}</td>
                    <td className="px-3 py-2">{r.StartTime ? new Date(r.StartTime).toLocaleString() : '—'}</td>
                    <td className="px-3 py-2">{fmt$(r.Turnover)}</td>
                    <td className="px-3 py-2">{fmt$(r.CreditWin)}</td>
                  </tr>
                ))}
                {!loading && sessions.length === 0 && (
                  <tr><td colSpan={8} className="px-3 py-6 text-center text-slate-500">No sessions. Select venue and Apply.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        {subTab === 'meter' && (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-3 py-2 w-10" />
                  <th className="px-3 py-2 text-left">SnapshotTime</th>
                  <th className="px-3 py-2 text-left">EgmPK</th>
                  <th className="px-3 py-2 text-left">Turnover</th>
                  <th className="px-3 py-2 text-left">CreditWin</th>
                  <th className="px-3 py-2 text-left">Stroke</th>
                  <th className="px-3 py-2 text-left">CashIn</th>
                  <th className="px-3 py-2 text-left">CashDrop</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 bg-white">
                {loading && (
                  <tr><td colSpan={8} className="px-3 py-6 text-center text-slate-500">Loading…</td></tr>
                )}
                {!loading && meterReadings.map((r) => {
                  const key = `${r.EgmPK}-${r.SnapshotTime}`
                  return (
                    <tr key={key} className="hover:bg-slate-50">
                      <td className="px-3 py-2">
                        <input
                          type="checkbox"
                          checked={selectedMeter.has(key)}
                          onChange={() => toggleMeter(key)}
                        />
                      </td>
                      <td className="px-3 py-2">{r.SnapshotTime ? new Date(r.SnapshotTime).toLocaleString() : '—'}</td>
                      <td className="px-3 py-2 font-mono">{r.EgmPK}</td>
                      <td className="px-3 py-2">{fmt$(r.Turnover)}</td>
                      <td className="px-3 py-2">{fmt$(r.CreditWin)}</td>
                      <td className="px-3 py-2">{r.Stroke ?? '—'}</td>
                      <td className="px-3 py-2">{fmt$(r.CashIn)}</td>
                      <td className="px-3 py-2">{fmt$(r.CashDrop)}</td>
                    </tr>
                  )
                })}
                {!loading && meterReadings.length === 0 && (
                  <tr><td colSpan={8} className="px-3 py-6 text-center text-slate-500">No meter readings. Select venue and Apply.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        {subTab === 'payouts' && (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-3 py-2 w-10" />
                  <th className="px-3 py-2 text-left">Time</th>
                  <th className="px-3 py-2 text-left">Payout</th>
                  <th className="px-3 py-2 text-left">Member</th>
                  <th className="px-3 py-2 text-left">EGM</th>
                  <th className="px-3 py-2 text-left">Venue</th>
                  <th className="px-3 py-2 text-left">TransactionID</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 bg-white">
                {loading && (
                  <tr><td colSpan={7} className="px-3 py-6 text-center text-slate-500">Loading…</td></tr>
                )}
                {!loading && payouts.map((r) => (
                  <tr key={r.TransactionPK} className="hover:bg-slate-50">
                    <td className="px-3 py-2">
                      <input
                        type="checkbox"
                        checked={selectedPayouts.has(String(r.TransactionPK))}
                        onChange={() => togglePayout(String(r.TransactionPK))}
                      />
                    </td>
                    <td className="px-3 py-2">{r.SnapShotTime ? new Date(r.SnapShotTime).toLocaleString() : '—'}</td>
                    <td className="px-3 py-2 font-semibold text-red-600">{fmt$(r.CashOut)}</td>
                    <td className="px-3 py-2 font-mono">{r.MemberID || 'Unidentified'}</td>
                    <td className="px-3 py-2">{r.EgmHouseNo || r.EgmName || '—'}</td>
                    <td className="px-3 py-2">{r.Venue || '—'}</td>
                    <td className="px-3 py-2 font-mono text-xs">{r.TransactionPK}</td>
                  </tr>
                ))}
                {!loading && payouts.length === 0 && (
                  <tr><td colSpan={7} className="px-3 py-6 text-center text-slate-500">No payouts. Select venue and Apply.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  )
}
